import { Tabs, TabsKinds } from "@minervaproject/fds/es/components/tabs";
import { groupBy, uniq } from "lodash";
import { useEffect, useState } from "react";

import { BreakoutControls } from "./breakout_controls";

import { ParticipantAv } from "components/av/participant_av";
import { BreakoutRoomsList } from "components/breakout_control_room/breakout_rooms_list";
import { BreakoutRoomsPreview } from "components/breakout_control_room/breakout_rooms_preview";
import { WorkbookAnswers } from "components/breakout_control_room/workbook_answers";
import { WorkbookDataController } from "components/breakout_control_room/workbook_data_controller";
import { WorkbookFlavors } from "components/breakout_control_room/workbook_flavors";
import { WorkbookQuestions } from "components/breakout_control_room/workbook_questions";
import { CenteredErrorBoundary, FlightErrorBoundary } from "components/error_boundary";
import { useAppDispatch, useAppSelector, useArraySelector } from "hooks/redux_hooks";
import { useSidebarAdjustment } from "hooks/use_sidebar_adjustment";
import { PROFILE_PROPERTIES, useUsersProperties } from "hooks/use_user_properties";
import { ResourceTypeType } from "shared/types";
import { MonitoringEventTypeType } from "shared/types/monitoring_event";
import { UserType } from "shared/types/user";
import { classroomSelectors, localSelectors } from "store";
import { createMonitoringEvent } from "store/reducers/monitoring";

const MAX_NUMBER_OF_GROUPS_FOR_GROUP_PREVIEW = 10;
const TAB_GROUP_PREVIEW_LABELS = ["group-preview", "Group Preview"];
const TAB_GROUP_LIST_LABELS = ["group-list", "Group List"];
const TAB_QUESTIONS_LABELS = ["questions", "Questions"];
const TAB_ALL_ANSWERS_LABELS = ["all-answers", "All Answers"];

function BreakoutControlRoom({ breakoutSessionId }: { breakoutSessionId: string }) {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const breakoutSession = useAppSelector(
    classroomSelectors.selectBreakoutSessionById(breakoutSessionId),
  );
  const breakoutId = breakoutSession.breakout_id;
  const breakout = useAppSelector(classroomSelectors.selectBreakoutById(breakoutId));
  const breakoutRooms = useArraySelector(
    classroomSelectors.selectBreakoutRoomsByBreakoutId(breakoutId),
  );
  const allUsers = Object.values(
    useUsersProperties(PROFILE_PROPERTIES.concat("is_present")),
  ) as UserType[];
  const allResourceIds = breakoutRooms.flatMap((breakoutRoom) => breakoutRoom.resource_ids);
  const allResources = useArraySelector(classroomSelectors.selectResourcesByIds(allResourceIds));
  const widthAdjustment = useSidebarAdjustment();
  const showGroupPreview = breakoutRooms.length <= MAX_NUMBER_OF_GROUPS_FOR_GROUP_PREVIEW;
  const [selectedTab, setSelectedTab] = useState(
    showGroupPreview ? TAB_GROUP_PREVIEW_LABELS[0] : TAB_GROUP_LIST_LABELS[0],
  );

  useEffect(() => {
    dispatch(
      createMonitoringEvent({
        type: MonitoringEventTypeType.SHOW_BREAKOUT_CONTROL_ROOM,
        userId: currentUserId,
        contextData: { breakoutSessionId },
      }),
    );
  }, [dispatch, currentUserId, breakoutSessionId]);

  // Users in the control room should hear each other.
  const otherUsersInControlRoom = allUsers.filter((user) => {
    if (user.id === currentUserId || !user.is_present) return false;
    return !breakoutRooms.some(
      (breakoutRoom) =>
        breakoutRoom.assigned_user_ids.includes(user.id) ||
        breakoutRoom.visiting_user_ids.includes(user.id),
    );
  });

  if (!breakout) return null;

  const allBreakoutNotes = allResources.filter((resource) => resource.is_breakout_group_notes);
  const breakoutRoomsByFlavor = groupBy(breakoutRooms, "flavor");
  const showWorkbookData = allBreakoutNotes[0]?.type === ResourceTypeType.ForumWorkbook;
  const breakoutTabLabels = [TAB_GROUP_LIST_LABELS];
  if (showGroupPreview) {
    breakoutTabLabels.unshift(TAB_GROUP_PREVIEW_LABELS);
  }
  if (showWorkbookData) {
    breakoutTabLabels.push(TAB_QUESTIONS_LABELS, TAB_ALL_ANSWERS_LABELS);
  }

  // Check for a special case where the breakout has flavors, each flavor has the same resource,
  // and resources are shared between groups. This setup might not actually be used in real classes,
  // but we should still handle it.
  const hasOneResourceForAllFlavorsAndGroups =
    breakout.has_flavors &&
    breakout.share_resource_between_groups &&
    uniq(allBreakoutNotes.map((resource) => resource.id)).length === 1;

  return (
    <div
      className="flex-auto flex flex-column bg-black-tint-10 body-s text-black-tint-70"
      style={{
        width: widthAdjustment,
      }}
      data-testid="breakout-control-room"
    >
      <div className="flex items-center justify-between">
        <Tabs
          className="py2 pr2 pl4"
          kind={TabsKinds.Dark}
          tabs={breakoutTabLabels}
          currentTab={selectedTab}
          setCurrentTab={setSelectedTab}
        />
        <FlightErrorBoundary text="The breakout controls cannot be displayed">
          <BreakoutControls breakoutSessionId={breakoutSessionId} />
        </FlightErrorBoundary>
      </div>

      <div className="flex-auto pb4 pt2 vertical-scroll">
        {selectedTab === TAB_GROUP_PREVIEW_LABELS[0] && (
          <CenteredErrorBoundary text="The group preview cannot be displayed">
            <BreakoutRoomsPreview breakoutRooms={breakoutRooms} />
          </CenteredErrorBoundary>
        )}

        {selectedTab === TAB_GROUP_LIST_LABELS[0] && (
          <CenteredErrorBoundary text="The group list cannot be displayed">
            <BreakoutRoomsList
              breakoutRooms={breakoutRooms}
              showFlavors={breakout.has_flavors}
              showWorkbookData={showWorkbookData && !breakout.share_resource_between_groups}
            />
          </CenteredErrorBoundary>
        )}

        {selectedTab === TAB_QUESTIONS_LABELS[0] && (
          <CenteredErrorBoundary text="The questions cannot be displayed">
            {breakout.has_flavors ? (
              <WorkbookFlavors
                breakoutRoomsByFlavor={breakoutRoomsByFlavor}
                type="questions"
                shareResourceBetweenGroups={breakout.share_resource_between_groups}
                hasOneResourceForAllFlavorsAndGroups={hasOneResourceForAllFlavorsAndGroups}
              />
            ) : (
              <WorkbookQuestions />
            )}
          </CenteredErrorBoundary>
        )}

        {selectedTab === TAB_ALL_ANSWERS_LABELS[0] && (
          <CenteredErrorBoundary text="The answers cannot be displayed">
            {breakout.has_flavors ? (
              <WorkbookFlavors
                breakoutRoomsByFlavor={breakoutRoomsByFlavor}
                type="answers"
                shareResourceBetweenGroups={breakout.share_resource_between_groups}
                hasOneResourceForAllFlavorsAndGroups={hasOneResourceForAllFlavorsAndGroups}
              />
            ) : (
              <WorkbookAnswers
                breakoutRooms={breakoutRooms}
                shareResourceBetweenGroups={breakout.share_resource_between_groups}
              />
            )}
          </CenteredErrorBoundary>
        )}

        {showWorkbookData && selectedTab !== TAB_GROUP_PREVIEW_LABELS[0] && (
          <FlightErrorBoundary
            text="There was a problem fetching the workbook content"
            margin="8px"
          >
            {Object.entries(breakoutRoomsByFlavor).map(([flavor, _breakoutRooms]) => (
              <WorkbookDataController
                key={flavor}
                breakout={breakout}
                breakoutRooms={_breakoutRooms}
                allBreakoutNotes={allBreakoutNotes}
              />
            ))}
          </FlightErrorBoundary>
        )}
      </div>

      <div>
        {otherUsersInControlRoom.map((user) => (
          <div key={user.id} className="relative" style={{ width: 0, aspectRatio: "1/1" }}>
            <ParticipantAv
              userId={user.id}
              isSelf={false}
              includeVideo={false}
              isParticipantHidden
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export { BreakoutControlRoom };
